import React, { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/shell/PageHeader";
import { useAppState } from "@/state/AppStateProvider";
import { SkillBadge } from "@/components/badges/SkillBadge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Search, FileCode2, Save, Eye, Pencil, Copy, Download, Upload, FileText, AlertTriangle, Trash2 } from "lucide-react";
import { SKILL_CATEGORY_LABELS, SKILL_CATEGORIES } from "@/lib/constants";

// A user-created skill needs a compile phase; derive it from the friendly category.
const PHASE_FOR_CATEGORY = { style: "generation", industry: "generation", critique: "critique" };
import { buildSkillFromManualInput, buildSkillFromMarkdown } from "@/lib/skillBuilder";
import { MarkdownView, parseFrontmatter } from "@/components/common/MarkdownView";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const DEFAULT_NEW_SKILL_BODY = `## When to use

Use this skill when...

## Instructions

- Define the thinking pattern.
- Specify what to avoid.
- Describe the output shape.

## Output format

Plain prose, bullets, YAML, or another format the agent should follow.
`;

export default function Skills() {
  const { state, toggleSkill, updateSkill, addSkill, removeSkill } = useAppState();
  const [selectedId, setSelectedId] = useState(state.skills[0]?.id || null);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [mode, setMode] = useState("view"); // view | edit
  const [draft, setDraft] = useState("");
  const [newSkillOpen, setNewSkillOpen] = useState(false);
  const [newSkillMode, setNewSkillMode] = useState("manual");
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newCategory, setNewCategory] = useState("style");
  const [newBody, setNewBody] = useState(DEFAULT_NEW_SKILL_BODY);
  const [uploadMarkdown, setUploadMarkdown] = useState("");
  const [uploadFilename, setUploadFilename] = useState("");
  const [uploadError, setUploadError] = useState("");

  const filtered = useMemo(() => {
    let l = state.skills;
    if (categoryFilter !== "all") l = l.filter((s) => s.category === categoryFilter);
    if (q.trim()) {
      const t = q.toLowerCase();
      l = l.filter((s) => `${s.name} ${s.description} ${s.markdown}`.toLowerCase().includes(t));
    }
    return l;
  }, [state.skills, q, categoryFilter]);

  const skillSummary = useMemo(() => {
    const enabled = state.skills.filter((skill) => skill.enabled).length;
    const categoryCounts = SKILL_CATEGORIES.map((category) => ({
      category,
      label: SKILL_CATEGORY_LABELS[category],
      count: state.skills.filter((skill) => skill.category === category).length,
    }));
    return { total: state.skills.length, enabled, categoryCounts };
  }, [state.skills]);

  const selected = useMemo(() => state.skills.find((s) => s.id === selectedId) || filtered[0] || state.skills[0], [state.skills, selectedId, filtered]);
  const selectedKey = selected?.id || "";
  const selectedMarkdown = selected?.markdown || "";

  useEffect(() => {
    setDraft(selectedMarkdown);
  }, [selectedKey, selectedMarkdown, mode]);

  const onSave = () => {
    if (!selected) return;
    const { frontmatter } = parseFrontmatter(draft);
    updateSkill(selected.id, { markdown: draft, name: frontmatter.name || selected.name, description: frontmatter.description || selected.description, phase: frontmatter.phase || selected.phase, category: frontmatter.category || selected.category });
    setMode("view");
    toast.success(`Saved ${frontmatter.name || selected.name}.md`);
  };

  const onCopy = () => {
    navigator.clipboard?.writeText(selected?.markdown || "");
    toast.success("Markdown copied");
  };

  const onDownload = () => {
    if (!selected) return;
    const blob = new Blob([selected.markdown || ""], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${selected.name}.md`;
    a.click();
  };

  // Only user-authored skills (sk_custom_*) can be deleted — the seeded library
  // is toggle-on/off only (and would just be backfilled again on next login).
  const isCustomSkill = (skill) => Boolean(skill?.id?.startsWith("sk_custom_"));
  const onDelete = () => {
    if (!selected || !isCustomSkill(selected)) return;
    if (!window.confirm(`Delete ${selected.name}.md? This can't be undone.`)) return;
    const name = selected.name;
    removeSkill(selected.id);
    setSelectedId(null);
    setMode("view");
    toast.success(`${name}.md deleted`);
  };

  const resetNewSkillForm = () => {
    setNewSkillMode("manual");
    setNewName("");
    setNewDescription("");
    setNewCategory("style");
    setNewBody(DEFAULT_NEW_SKILL_BODY);
    setUploadMarkdown("");
    setUploadFilename("");
    setUploadError("");
  };

  const selectCreatedSkill = (skill) => {
    addSkill(skill);
    setSelectedId(skill.id);
    setCategoryFilter("all");
    setMode("view");
    setNewSkillOpen(false);
    resetNewSkillForm();
    toast.success(`${skill.name}.md added`);
  };

  const onPickSkillFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!/\.(md|markdown)$/i.test(file.name)) {
      setUploadError("Upload a .md or .markdown file.");
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setUploadMarkdown(String(reader.result || ""));
      setUploadFilename(file.name);
      setUploadError("");
      toast.success(`${file.name} loaded`);
    };
    reader.onerror = () => setUploadError("Could not read that markdown file.");
    reader.readAsText(file);
    event.target.value = "";
  };

  const onCreateManualSkill = () => {
    if (!newName.trim()) {
      toast.error("Give the skill a name first");
      return;
    }
    selectCreatedSkill({
      ...buildSkillFromManualInput({
        name: newName,
        description: newDescription,
        phase: PHASE_FOR_CATEGORY[newCategory] || "generation",
        body: newBody,
      }),
      category: newCategory,
    });
  };

  const onCreateUploadedSkill = () => {
    if (!uploadMarkdown.trim()) {
      setUploadError("Choose a markdown skill file first.");
      return;
    }
    const built = buildSkillFromMarkdown(uploadMarkdown, {
      filename: uploadFilename || "skill.md",
      source: "upload",
    });
    // The generation recipe selects skills by category (style|industry|critique);
    // a null category would make the uploaded skill inert. Derive from phase.
    selectCreatedSkill({
      ...built,
      category: built.category || (built.phase === "critique" ? "critique" : "style"),
    });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="skills-page">
      <PageHeader
        eyebrow="Skills"
        title="Tune how the agent thinks, writes, and reviews."
        testId="skills-page-header"
        description="Reusable .md files that define how the agent thinks and writes. The agent handles platform-specific layout automatically per channel."
        actions={
          <>
            <Button variant="outline" className="gap-1.5" onClick={() => document.querySelector('[data-testid="skills-search"]')?.focus()}>
              <Search className="h-4 w-4" /> Search skills
            </Button>
            <Button className="gap-1.5" data-testid="skills-new" onClick={() => setNewSkillOpen(true)} style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
              <Plus className="h-4 w-4" /> New skill.md
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        {/* LEFT — skill list */}
        <aside className="xl:col-span-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search skills…" className="h-9 pl-8" data-testid="skills-search" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
            </div>
          </div>
          <p className="text-[11.5px] text-muted-foreground">
            <span className="font-semibold text-1">{skillSummary.enabled}</span> of {skillSummary.total} skills enabled
          </p>
          <Tabs value={categoryFilter} onValueChange={setCategoryFilter}>
            <TabsList className="bg-transparent p-0 gap-1 flex-wrap h-auto">
              <TabsTrigger value="all" className="h-7 gap-1.5 px-2 text-xs">
                All <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{skillSummary.total}</span>
              </TabsTrigger>
              {SKILL_CATEGORIES.map((p) => (
                <TabsTrigger key={p} value={p} className="h-7 gap-1.5 px-2 text-xs">
                  {SKILL_CATEGORY_LABELS[p]} <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{skillSummary.categoryCounts.find((c) => c.category === p)?.count ?? 0}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="flex flex-col gap-1.5">
            {filtered.map((s) => {
              const active = selected?.id === s.id;
              return (
                <div
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => { setSelectedId(s.id); setMode("view"); }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedId(s.id);
                      setMode("view");
                    }
                  }}
                  data-testid={`skill-card-${s.id}`}
                  className={cn(
                    "group flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors",
                    active ? "border-[color:var(--accent-cyan)]/45 bg-white/[0.04]" : "hover:border-[color:var(--accent-cyan)]/25 hover:bg-white/[0.025]",
                  )}
                  style={{ borderColor: active ? undefined : "var(--border-subtle)", backgroundColor: active ? undefined : "var(--surface-1)" }}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
                    <FileCode2 className="h-4 w-4 text-[color:var(--accent-cyan)]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate font-mono text-[12.5px] font-semibold text-1">{s.name}.md</p>
                      <Switch checked={s.enabled} onCheckedChange={(e) => { /* prevent navigation */ toggleSkill(s.id); }} onClick={(e) => e.stopPropagation()} data-testid={`skill-toggle-${s.id}`} />
                    </div>
                    <p className="mt-1 text-[12px] text-muted-foreground line-clamp-2">{s.description}</p>
                    <div className="mt-2"><SkillBadge category={s.category} phase={s.phase} /></div>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
                No skills match.
              </div>
            )}
          </div>
        </aside>

        {/* RIGHT — viewer / editor */}
        <section className="xl:col-span-8" data-testid="skill-viewer">
          {!selected ? (
            <div className="rounded-xl border border-dashed p-10 text-center text-sm text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
              Select a skill to view its .md file.
            </div>
          ) : (
            <div className="rounded-xl border bg-card inner-highlight overflow-hidden" style={{ borderColor: "var(--border-subtle)" }}>
              <header className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md border" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}>
                    <FileCode2 className="h-4 w-4 text-[color:var(--accent-cyan)]" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-mono text-[13px] font-semibold text-1 truncate">{selected.name}.md</p>
                    <p className="text-[11.5px] text-muted-foreground truncate">{selected.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="mr-1 inline-flex rounded-md border p-0.5" style={{ borderColor: "var(--border-subtle)" }}>
                    <button type="button" onClick={() => setMode("view")} data-testid="skill-mode-view" className={cn("inline-flex items-center gap-1 rounded px-2 py-1 text-[11.5px]", mode === "view" ? "bg-white/[0.08] text-1" : "text-muted-foreground")}><Eye className="h-3 w-3" /> View</button>
                    <button type="button" onClick={() => setMode("edit")} data-testid="skill-mode-edit" className={cn("inline-flex items-center gap-1 rounded px-2 py-1 text-[11.5px]", mode === "edit" ? "bg-white/[0.08] text-1" : "text-muted-foreground")}><Pencil className="h-3 w-3" /> Edit</button>
                  </div>
                  <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={onCopy} data-testid="skill-copy"><Copy className="h-3 w-3" /> Copy</Button>
                  <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={onDownload} data-testid="skill-download"><Download className="h-3 w-3" /> .md</Button>
                  {isCustomSkill(selected) && (
                    <Button size="sm" variant="outline" className="h-8 gap-1.5 text-red-300 hover:text-red-200" onClick={onDelete} data-testid="skill-delete"><Trash2 className="h-3 w-3" /> Delete</Button>
                  )}
                  {mode === "edit" && (
                    <Button size="sm" className="h-8 gap-1.5" onClick={onSave} data-testid="skill-editor-save" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}><Save className="h-3 w-3" /> Save</Button>
                  )}
                </div>
              </header>

              {mode === "view" ? (
                <div className="px-5 py-4">
                  <Frontmatter md={selected.markdown} />
                  <div className="mt-4">
                    <MarkdownView source={parseFrontmatter(selected.markdown).body} />
                  </div>
                </div>
              ) : (
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={28}
                  className="rounded-none border-0 font-mono text-[12.5px] leading-[1.7] focus-visible:ring-0"
                  style={{ backgroundColor: "var(--surface-1)", color: "var(--text-1)" }}
                  data-testid="skill-editor-textarea"
                />
              )}
            </div>
          )}
        </section>
      </div>

      <Dialog open={newSkillOpen} onOpenChange={(open) => { setNewSkillOpen(open); if (!open) resetNewSkillForm(); }}>
        <DialogContent className="max-w-[760px] overflow-hidden border bg-[color:var(--surface-1)] p-0 text-1" data-testid="skill-create-dialog" style={{ borderColor: "var(--border-subtle)" }}>
          <DialogHeader className="border-b px-5 py-4" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileCode2 className="h-4 w-4 text-[color:var(--accent-cyan)]" />
              New skill.md
            </DialogTitle>
            <DialogDescription className="text-[12.5px] text-muted-foreground">
              Add a single markdown skill for the MVP. Skill folders with scripts can come later as a bundled skill format.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-5 py-4">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setNewSkillMode("manual")}
                className={cn(
                  "rounded-xl border p-4 text-left transition-colors",
                  newSkillMode === "manual" ? "border-[color:var(--accent-cyan)] bg-white/[0.055]" : "hover:bg-white/[0.035]",
                )}
                style={{ borderColor: newSkillMode === "manual" ? undefined : "var(--border-subtle)" }}
                data-testid="skill-create-mode-manual"
              >
                <span className="flex items-center gap-2 text-sm font-semibold"><FileText className="h-4 w-4 text-[color:var(--accent-cyan)]" /> Create skill</span>
                <span className="mt-1 block text-[12px] leading-relaxed text-muted-foreground">Write the frontmatter and instructions from inside the app.</span>
              </button>
              <button
                type="button"
                onClick={() => setNewSkillMode("upload")}
                className={cn(
                  "rounded-xl border p-4 text-left transition-colors",
                  newSkillMode === "upload" ? "border-[color:var(--accent-cyan)] bg-white/[0.055]" : "hover:bg-white/[0.035]",
                )}
                style={{ borderColor: newSkillMode === "upload" ? undefined : "var(--border-subtle)" }}
                data-testid="skill-create-mode-upload"
              >
                <span className="flex items-center gap-2 text-sm font-semibold"><Upload className="h-4 w-4 text-[color:var(--accent-cyan)]" /> Upload .md</span>
                <span className="mt-1 block text-[12px] leading-relaxed text-muted-foreground">Import a markdown skill file and keep it editable here.</span>
              </button>
            </div>

            <div className="rounded-lg border p-3" style={{ borderColor: "rgba(246,178,74,0.26)", backgroundColor: "rgba(246,178,74,0.08)" }}>
              <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-foreground/85">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[color:var(--accent-amber)]" />
                MVP skills are single .md files. Folder-based skills with companion scripts should be treated as a post-MVP capability.
              </p>
            </div>

            {newSkillMode === "manual" ? (
              <div className="space-y-4" data-testid="skill-create-manual-panel">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="skill-create-name" className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Name</Label>
                    <Input
                      id="skill-create-name"
                      value={newName}
                      onChange={(event) => setNewName(event.target.value)}
                      placeholder="NoHypeCTA"
                      data-testid="skill-create-name"
                      style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Category</Label>
                    <div className="flex flex-wrap gap-1.5" data-testid="skill-create-category">
                      {SKILL_CATEGORIES.map((category) => (
                        <button
                          key={category}
                          type="button"
                          onClick={() => setNewCategory(category)}
                          className={cn(
                            "rounded-md border px-2.5 py-1.5 text-[11.5px] transition-colors",
                            newCategory === category ? "border-[color:var(--accent-cyan)] bg-white/[0.07] text-1" : "text-muted-foreground hover:bg-white/[0.035]",
                          )}
                          style={{ borderColor: newCategory === category ? undefined : "var(--border-subtle)" }}
                          data-testid={`skill-create-category-${category}`}
                        >
                          {SKILL_CATEGORY_LABELS[category]}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="skill-create-description" className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Description</Label>
                  <Input
                    id="skill-create-description"
                    value={newDescription}
                    onChange={(event) => setNewDescription(event.target.value)}
                    placeholder="What this skill helps the agent do."
                    data-testid="skill-create-description"
                    style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="skill-create-body" className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Instructions</Label>
                  <Textarea
                    id="skill-create-body"
                    value={newBody}
                    onChange={(event) => setNewBody(event.target.value)}
                    rows={10}
                    className="font-mono text-[12.5px] leading-[1.65]"
                    data-testid="skill-create-body"
                    style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4" data-testid="skill-create-upload-panel">
                <label
                  className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed px-4 py-8 text-center transition-colors hover:bg-white/[0.035]"
                  style={{ borderColor: uploadError ? "rgba(248,113,113,0.45)" : "var(--border-subtle)" }}
                  htmlFor="skill-upload-input"
                >
                  <Upload className="h-7 w-7 text-[color:var(--accent-cyan)]" />
                  <span className="mt-3 text-sm font-semibold">{uploadFilename || "Choose a markdown skill"}</span>
                  <span className="mt-1 text-[12px] text-muted-foreground">Supports .md and .markdown files.</span>
                  <input id="skill-upload-input" type="file" accept=".md,.markdown,text/markdown,text/plain" className="hidden" onChange={onPickSkillFile} data-testid="skill-upload-input" />
                </label>
                {uploadError && <p className="text-[12px] text-red-400" data-testid="skill-upload-error">{uploadError}</p>}
                {uploadMarkdown && (
                  <div className="rounded-lg border p-3" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }} data-testid="skill-upload-preview">
                    <p className="text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground">Preview</p>
                    <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap font-mono text-[12px] leading-relaxed text-foreground/80 scrollbar-thin">
                      {uploadMarkdown.slice(0, 1400)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="border-t px-5 py-4" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
            <Button type="button" variant="outline" onClick={() => setNewSkillOpen(false)} data-testid="skill-create-cancel">Cancel</Button>
            <Button
              type="button"
              onClick={newSkillMode === "manual" ? onCreateManualSkill : onCreateUploadedSkill}
              data-testid="skill-create-save"
              style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}
            >
              {newSkillMode === "manual" ? "Create skill" : "Import skill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

function Frontmatter({ md }) {
  const { frontmatter } = parseFrontmatter(md);
  if (!Object.keys(frontmatter).length) return null;
  return (
    <div className="rounded-lg border bg-[color:var(--surface-1)] p-3 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Frontmatter</p>
      <dl className="mt-1.5 grid grid-cols-1 gap-1 font-mono text-[12px] md:grid-cols-2">
        {Object.entries(frontmatter).map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <dt className="text-[color:var(--accent-cyan)]">{k}:</dt>
            <dd className="text-foreground/85 truncate">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

